import { Component } from "@theme/component";
import { requestIdleCallback } from "@theme/utilities";

const SWIPER_VERSION = "12.1.2";
const SWIPER_BASE_URL = `https://cdn.jsdelivr.net/npm/swiper@${SWIPER_VERSION}`;

export class CarouselComponent extends Component {
  async connectedCallback() {
    super.connectedCallback();

    const media = this.getAttribute("data-carousel-media");
    if (media) {
      this.carouselMedia = window.matchMedia(media);
      this.onCarouselMediaChange = () => this.syncSwiper();
      this.carouselMedia.addEventListener("change", this.onCarouselMediaChange);
    }
    // A click anywhere on a partially-visible slide nudges it fully into view
    // instead of acting (MA-2986). Capture phase so it runs before the card's
    // own link / option / add handlers and before Swiper's click handler on
    // `.swiper`. Bound once here; it no-ops until Swiper is ready, so no
    // re-binding is needed after init.
    this.addEventListener("click", this.#onSlideClick, true);

    // Defer Swiper initialization until the browser is idle
    requestIdleCallback(() => this.syncSwiper());
  }

  async syncSwiper() {
    if (!this.isConnected) return;
    if (this.carouselMedia && !this.carouselMedia.matches) {
      this.swiper?.destroy(true, true);
      this.swiper = null;
      return;
    }

    await this.loadSwiper();
    if (!this.isConnected || (this.carouselMedia && !this.carouselMedia.matches)) return;
    this.initSwiper();
  }

  /**
   * When the clicked slide is not fully within the carousel viewport, swallows
   * the click (so the product link, option pill or Add button does nothing) and
   * slides the carousel one step toward it. A second click on the now-visible
   * card behaves normally.
   *
   * @param {MouseEvent} event
   */
  #onSlideClick = event => {
    if (!this.swiper) return;

    // Peek layouts already hand this to Swiper's `slideToClickedSlide`.
    if (this.swiper.params?.slideToClickedSlide) return;

    // A drag that ends over a slide also emits a click; Swiper flags it via
    // `allowClick`. Leave those (and clicks mid-transition) alone.
    if (this.swiper.allowClick === false || this.swiper.animating) return;

    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== "function") return;

    const slide = /** @type {HTMLElement | null} */ (target.closest(".swiper-slide"));
    if (!slide || !this.contains(slide)) return;

    if (this.#bringSlideIntoView(slide)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * Nudges the carousel one slide toward `slide` when it is only partially
   * visible within the `.swiper` viewport. A single snap step is always enough:
   * a slide the visitor could click is cut off by less than one slide width.
   * Uses the geometry rather than Swiper's `swiper-slide-fully-visible` class,
   * which is only maintained when `watchSlidesProgress` is enabled (peek
   * layouts only).
   *
   * @param {HTMLElement} slide
   * @returns {boolean} true when the slide was cut off and a nudge was issued
   */
  #bringSlideIntoView(slide) {
    const viewport = this.querySelector(".swiper");
    if (!viewport) return false;

    const slideRect = slide.getBoundingClientRect();
    const viewRect = viewport.getBoundingClientRect();
    const TOLERANCE = 2; // px — ignore sub-pixel rounding

    const cutOffRight = slideRect.right > viewRect.right + TOLERANCE;
    const cutOffLeft = slideRect.left < viewRect.left - TOLERANCE;
    if (!cutOffRight && !cutOffLeft) return false;

    // Move a single slide instead of `slideTo(index)`, which snaps the clicked
    // slide to its own position (re-centering the whole carousel). Guard the
    // bounds so `rewind: true` layouts can't wrap around to the far end.
    if (cutOffRight && (this.swiper.params?.loop || !this.swiper.isEnd)) {
      this.swiper.slideNext();
      return true;
    }
    if (cutOffLeft && (this.swiper.params?.loop || !this.swiper.isBeginning)) {
      this.swiper.slidePrev();
      return true;
    }
    return false;
  }

  async loadSwiper() {
    if (window.Swiper && window.SwiperModules) {
      this.SwiperClass = window.Swiper;
      this.SwiperModules = window.SwiperModules;
      return;
    }

    const styles = ["swiper", "navigation", "pagination", "a11y"];
    styles.forEach(style => this.loadStyle(style));

    try {
      const modules = ["navigation", "pagination", "autoplay", "a11y"];
      const imports = [
        import(`${SWIPER_BASE_URL}/swiper.min.mjs`),
        ...modules.map(m => import(`${SWIPER_BASE_URL}/modules/${m}.min.mjs`)),
      ];

      const [{ default: Swiper }, ...loadedModules] = await Promise.all(imports);

      window.Swiper = Swiper;
      window.SwiperModules = loadedModules.map(m => m.default);

      this.SwiperClass = window.Swiper;
      this.SwiperModules = window.SwiperModules;
    } catch (err) {
      console.error("Swiper load failed", err);
    }
  }

  /**
   * @param {string} name
   */
  loadStyle(name) {
    const id = `swiper-${name}-css`;
    if (document.getElementById(id)) return;

    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `${SWIPER_BASE_URL}/${name === "swiper" ? "" : "modules/"}${name}.min.css`;
    document.head.appendChild(link);
  }

  initSwiper() {
    if (!this.isConnected || (this.carouselMedia && !this.carouselMedia.matches)) return;
    const container = this.querySelector(".swiper");
    if (!container || !this.SwiperClass) return;

    // Destroy existing instance if it exists (relevant for Theme Editor updates)
    if (this.swiper) {
      this.swiper.destroy();
    }

    const settings = JSON.parse(this.getAttribute("data-settings") || "{}");
    const peekLayout = settings.peekLayout || false;
    const slideCenter = settings.slideCenter || false;
    const slideCount = container.querySelectorAll(".swiper-slide").length;
    const peekInitialSlide = slideCount > 2 ? 1 : 0;
    const peekGapMobile = Math.min(settings.spaceBetweenMobile || 8, 8);
    const peekGapTablet = Math.min(settings.spaceBetweenTablet || 12, 12);
    const peekGapDesktop = Math.min(settings.spaceBetweenDesktop || 16, 16);
    const last_slide_desktop_padding_right = settings.desktop_padding_right || 0;
    const last_slide_mobile_padding_right = settings.mobile_padding_right || 0;

    // On touch devices, hand swiping over to the browser's native scroller
    // (CSS Scroll Snap) instead of Swiper's JS touch-tracking. The JS mode
    // moves the wrapper from main-thread touchmove handlers, so on real
    // phones the carousel trails the finger whenever the thread is busy
    // (MA-2936). cssMode stays off for fine-pointer devices because it
    // disables mouse dragging; `speed` only applies there, as cssMode
    // scrolls with native momentum.
    const nativeTouchScroll = window.matchMedia("(hover: none) and (pointer: coarse)").matches;

    const swiperOptions = {
      modules: this.SwiperModules,
      cssMode: nativeTouchScroll,
      slidesPerView: peekLayout ? 1.06 : settings.slidesPerViewMobile || 1.5,
      spaceBetween: peekLayout ? peekGapMobile : (settings.spaceBetweenMobile ?? 8),
      initialSlide: peekLayout ? peekInitialSlide : 0,
      loop: peekLayout ? false : settings.loop || false,
      rewind: peekLayout,
      slidesOffsetAfter: last_slide_mobile_padding_right,
      centeredSlides: peekLayout,
      speed: peekLayout ? 450 : 300,
      autoplay: settings.autoplay
        ? {
            delay: (settings.autoplaySpeed || 5) * 1000,
            disableOnInteraction: false,
          }
        : false,
      pagination: settings.pagination
        ? {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
          }
        : false,
      slideToClickedSlide: peekLayout,
      watchSlidesProgress: peekLayout,
      navigation: settings.navigation
        ? {
            nextEl: settings.customArrows
              ? document.querySelector(settings.nextEl)
              : this.querySelector(".swiper-button-next"),
            prevEl: settings.customArrows
              ? document.querySelector(settings.prevEl)
              : this.querySelector(".swiper-button-prev"),
          }
        : false,
      breakpoints: {
        768: {
          slidesPerView: peekLayout ? 1.08 : settings.slidesPerViewTablet || 3,
          spaceBetween: peekLayout ? peekGapTablet : (settings.spaceBetweenTablet ?? 12),
          slidesOffsetAfter: last_slide_mobile_padding_right,
        },
        1280: {
          slidesPerView: peekLayout ? 1.1 : settings.slidesPerViewDesktop || 4,
          spaceBetween: peekLayout ? peekGapDesktop : (settings.spaceBetweenDesktop ?? 16),
          slidesOffsetAfter: last_slide_desktop_padding_right,
        },
      },
    };

    this.swiper = new this.SwiperClass(container, swiperOptions);
    if (slideCenter) {
      this.updateCentering();
      this.swiper.on("resize", () => {
        this.updateCentering();
      });

      this.swiper.on("update", () => {
        this.updateCentering();
      });
    }
    this.initPlayPause();
  }

  initPlayPause() {
    if (!this.swiper?.autoplay) return;

    const slideCount = this.swiper?.slides?.length || 0;
    const btn = /** @type {HTMLElement | null} */ (this?.querySelector("[data-carousel-play-pause]"));

    if (!btn) return;

    if (slideCount <= 1) {
      btn.style.display = "none";
      return;
    }

    const playLabel = btn.dataset.labelPlay;
    const pauseLabel = btn.dataset.labelPause;

    // prevent duplicate listeners (important in Shopify sections)
    if (btn.dataset.bound === "true") return;
    btn.dataset.bound = "true";

    const updateToPlay = () => {
      btn.setAttribute("data-paused", "");
      if (playLabel) btn.setAttribute("aria-label", playLabel);
    };

    const updateToPause = () => {
      btn.removeAttribute("data-paused");
      if (pauseLabel) btn.setAttribute("aria-label", pauseLabel);
    };

    btn.addEventListener("click", () => {
      if (this.swiper.autoplay.running) {
        this.swiper.autoplay.stop();
        updateToPlay();
      } else {
        this.swiper.autoplay.start();
        updateToPause();
      }
    });

    this.swiper.on("autoplayStop", updateToPlay);
    this.swiper.on("autoplayStart", updateToPause);
  }

  updateCentering() {
    if (!this.swiper) return;
    if (this.swiper.params.loop) {
      this.swiper.wrapperEl.style.justifyContent = "";
      return;
    }
    const shouldCenter = this.swiper.isLocked;

    this.swiper.wrapperEl.style.justifyContent = shouldCenter ? "center" : "";
  }

  disconnectedCallback() {
    this.carouselMedia?.removeEventListener("change", this.onCarouselMediaChange);
    this.removeEventListener("click", this.#onSlideClick, true);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    super.disconnectedCallback();
  }
}

if (!customElements.get("carousel-component")) {
  customElements.define("carousel-component", CarouselComponent);
}
